import React, { useMemo, useState } from "react";
import { isNil } from "ramda";
import { useIntl } from "react-intl";

import { FormControl, IconButton, InputAdornment, InputLabel, MenuItem, OutlinedInput, TextField } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import Checkbox from "@mui/material/Checkbox";
import LoadingButton from "@mui/lab/LoadingButton";

import { ErrorText, LinkBox, LoginContent, RememberContent, TextBox } from "../styled";
import { handleChanges } from "../utils/utils";
import palette from "../../../../config/colorPalette";
import { validateEmail, validatePassword } from "../../../utils/utils";
import ForgetPasswordModal from "./ForgetPasswordModal";

const LoginForm = ({ handleLogin, loading = false, testId, serviceData }) => {
  const intl = useIntl();
  const [username, setUsername] = useState();
  const [password, setPassword] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [usernameError, setUsernameError] = useState();
  const [passwordError, setPasswordError] = useState();

  const [serviceList, setServiceList] = useState([]);
  const [selectedServiceName, setSelectedServiceName] = useState("");
  const [selectedService, setSelectedService] = useState();

  const [forgotPasswordModal, setForgotPasswordModal] = useState(false);

  const disabled = useMemo(() => {
    return loading || !validateEmail(username) || !validatePassword(password);
  }, [loading, password, username]);

  const disabledNoMail = useMemo(() => {
    return loading || !validatePassword(password);
  }, [loading, password]);

  const auxDisabled =
    !isNil(selectedService) && selectedService.check_fields.includes("email") ? disabled : disabledNoMail;

  useMemo(() => {
    if (!isNil(serviceData) && !isNil(serviceData.login_services)) {
      const serviceNames = Object.keys(serviceData.login_services);
      setServiceList(serviceNames);

      const activeServiceName = serviceNames.find((name) => serviceData.login_services[name].active);

      const selectedName = activeServiceName || serviceNames[0];

      setSelectedServiceName(selectedName);
      setSelectedService(serviceData.login_services[selectedName]);
    }
  }, [serviceData]);

  return (
    <>
      <LoginContent>
        <TextBox size="2rem">{intl.formatMessage({ id: "welcome" })}</TextBox>
        {serviceList.length > 1 && (
          <>
            <TextBox size="1rem">{intl.formatMessage({ id: "select.service" })}</TextBox>
            <TextField
              variant="outlined"
              margin="dense"
              style={{ width: 150, alignSelf: "flex-start" }}
              defaultValue={selectedServiceName}
              value={selectedServiceName}
              select
              onChange={(e) => {
                const value = e.target.value;
                setSelectedServiceName(value);
                setSelectedService(serviceData.login_services[value]);
              }}
            >
              {serviceList.map((service, serviceIndex) => {
                if (!serviceData.login_services[service].active) {
                  return null;
                }
                return (
                  <MenuItem key={`${service}_${serviceIndex}`} value={service}>
                    {service.toUpperCase()}
                  </MenuItem>
                );
              })}
            </TextField>
          </>
        )}
        <TextBox size="1rem">{intl.formatMessage({ id: "login.username.text" })}</TextBox>
        <FormControl fullWidth variant="outlined">
          <InputLabel htmlFor="email">{intl.formatMessage({ id: "username" })}</InputLabel>
          <OutlinedInput
            id="email"
            type={!isNil(selectedService) && selectedService.check_fields.includes("email") ? "email" : "text"}
            required
            endAdornment={
              <InputAdornment position="end">
                <AlternateEmailIcon color="#e4e6ef" />
              </InputAdornment>
            }
            label={intl.formatMessage({ id: "username" })}
            value={username ?? ""}
            onChange={(event) =>
              handleChanges({
                who: "username",
                value: event.target.value,
                setUsernameError,
                setUsername,
                setPasswordError,
                setPassword,
                selectedService,
                intl,
              })
            }
            error={usernameError ? true : false}
            data-testid={`${testId}-email`}
          />
          {!isNil(usernameError) && <ErrorText>{intl.formatMessage({ id: usernameError })}</ErrorText>}
        </FormControl>
        <FormControl fullWidth variant="outlined">
          <InputLabel htmlFor="password">{intl.formatMessage({ id: "password" })}</InputLabel>
          <OutlinedInput
            id="password"
            type={showPassword ? "text" : "password"}
            required
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  onMouseDown={(event) => event.preventDefault()}
                  edge="end"
                  data-testid={`${testId}-visibility`}
                >
                  {showPassword ? <VisibilityOff color="#e4e6ef" /> : <Visibility color="#e4e6ef" />}
                </IconButton>
              </InputAdornment>
            }
            label={intl.formatMessage({ id: "password" })}
            value={password ?? ""}
            onChange={(event) =>
              handleChanges({
                who: "password",
                value: event.target.value,
                setUsernameError,
                setUsername,
                setPasswordError,
                setPassword,
                intl,
              })
            }
            error={passwordError ? true : false}
            data-testid={`${testId}-password`}
          />
          {!isNil(passwordError) && <ErrorText>{intl.formatMessage({ id: passwordError })}</ErrorText>}
        </FormControl>

        <RememberContent>
          <LinkBox color={palette.link}>
            <Checkbox checked={remember} onClick={() => setRemember(!remember)} sx={{ color: palette.link }} />
            {intl.formatMessage({ id: "remember.me" })}
          </LinkBox>
          <LinkBox
            id="forgot-password"
            onClick={() => {
              setForgotPasswordModal(true);
            }}
            color={palette.link}
          >
            {intl.formatMessage({ id: "forgot.password" })}
          </LinkBox>
        </RememberContent>
        <LoadingButton
          id="login-button"
          size="large"
          onClick={() => handleLogin(username, password, remember)}
          loading={loading ?? false}
          variant="contained"
          fullWidth
          disabled={
            !isNil(selectedService) && selectedService.check_fields.includes("email") ? disabled : disabledNoMail
          }
          style={{ backgroundColor: auxDisabled ? palette.light : palette.primary, fontFamily: "fagoco" }}
        >
          <span>{intl.formatMessage({ id: "login" })}</span>
        </LoadingButton>
        <TextBox size="0.75rem">
          {`${intl.formatMessage({ id: "trouble.message" })} `}
          <a
            style={{ color: palette.link, cursor: "pointer", textDecoration: "none" }}
            href="mailto:sysadmin@uptech-sensing.com"
          >
            sysadmin@uptech-sensing.com
          </a>
        </TextBox>
      </LoginContent>
      {forgotPasswordModal && <ForgetPasswordModal open={forgotPasswordModal} setOpen={setForgotPasswordModal} />}
    </>
  );
};
export default LoginForm;
